/**
 * CheckInNotificationBanner Component
 * Displays check-in reminder notifications in a visual banner
 */

import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';

const CheckInNotificationBanner = ({ notification, onDismiss, onActionClick }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!notification?.data?.due_time) return;

    const updateTimer = () => {
      const dueTime = new Date(notification.data.due_time);
      const now = new Date();
      const diff = Math.max(0, dueTime - now);
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      
      if (diff <= 0) {
        setTimeLeft('Now!');
      } else {
        setTimeLeft(`${minutes}m ${seconds}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [notification?.data?.due_time]);

  if (!isVisible) return null;

  return (
    <div className="fixed top-16 right-4 max-w-md animate-slideInRight">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 pt-0.5">
          <Icon name="Bell" className="h-6 w-6 animate-pulse" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm mb-1">
            {notification.title || 'Check-In Reminder'}
          </h3>
          <p className="text-blue-100 text-sm mb-2">
            {notification.message}
          </p>
          {timeLeft && (
            <p className="text-blue-50 text-xs font-medium">
              Time remaining: <span className="font-bold">{timeLeft}</span>
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex-shrink-0 flex gap-2 pt-0.5">
          <button
            onClick={() => {
              onActionClick?.(notification);
              setIsVisible(false);
            }}
            className="bg-white text-blue-600 hover:bg-blue-50 font-medium px-3 py-1 rounded text-xs transition-colors whitespace-nowrap"
          >
            Check In Now
          </button>
          <button
            onClick={() => {
              onDismiss?.(notification.id);
              setIsVisible(false);
            }}
            className="text-blue-100 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <Icon name="X" className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckInNotificationBanner;
